import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import {
  createAutodialerBucket,
  deleteAutodialerBucket,
  getAutodialerBuckets,
  updateAutodialerBucket,
} from '../../services/admin-page-service';
import type {
  AutodialerBucketConfiguration,
  AutodialerBucketConfigurationRequest,
  UploadFrequency,
} from '../../dto/admin-config-models';
import { SalesChannel } from '../../dto/model';
import { strings } from '../../lang';
import './autodialer-config-page.css';

const { Title } = Typography;

const SALES_CHANNEL_OPTIONS = Object.values(SalesChannel).map((v) => ({ label: v, value: v }));

const PRIORITY_VALUES = [
  41, 42, 43, 44, 45, 46, 47, 48, 49, 50,
  51, 52, 53, 54, 55, 56, 57, 58, 59, 60,
  61, 62, 63, 64, 65, 66, 68, 69, 70, 71,
  72, 73, 74, 75, 76, 77, 78, 79, 80, 81,
  82, 83, 84, 85, 86, 87, 88, 89, 90, 91,
  92, 93, 94, 95, 96, 97, 98, 99,
];
const PRIORITY_OPTIONS = PRIORITY_VALUES.map((v) => ({ label: String(v), value: v }));

const MAX_CALL_COUNT_OPTION = 40;
const CALL_COUNT_VALUES = Array.from({ length: MAX_CALL_COUNT_OPTION + 1 }, (_, i) => i);
const CALL_COUNT_OPTIONS = CALL_COUNT_VALUES.map((v) => ({ label: String(v), value: v }));

const EXCLUSIVITY_HINT = 'Priorities and Call Counts are mutually exclusive — clear one to use the other.';

interface FormValues {
  name: string;
  description?: string;
  autodialerBucketId: string;
  autodialerBucketName: string;
  salesChannels: string[];
  priorities: number[];
  callCounts?: number[];
  uploadFrequency: UploadFrequency;
  opportunityStatus?: string;
  isActive: boolean;
}

export const AutodialerBucketsConfig = () => {
  const labels = strings.autodialerSettings.bucketConfig;
  const uploadFrequencyOptions: { label: string; value: UploadFrequency }[] = [
    { label: labels.none, value: 'None' }, { label: labels.hourly, value: 'Hourly' },
    { label: labels.realTime, value: 'RealTime' }, { label: labels.manual, value: 'Manual' },
  ];
  const opportunityStatusOptions = [
    { label: labels.firstTimeScheduling, value: 'FirstTimeScheduling' }, { label: labels.reschedulingNeeded, value: 'ReschedulingNeeded' },
  ];
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [form] = Form.useForm<FormValues>();

  const [buckets, setBuckets] = useState<AutodialerBucketConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState<AutodialerBucketConfiguration | null>(null);
  const [saving, setSaving] = useState(false);

  const selectedChannels: string[] = Form.useWatch('salesChannels', form) ?? [];
  const isActiveForm: boolean = Form.useWatch('isActive', form) ?? false;
  const watchedBucketName: string = Form.useWatch('autodialerBucketName', form) ?? '';
  const uploadFrequencyWatcher: UploadFrequency = Form.useWatch('uploadFrequency', form) ?? 'None';
  const watchedPriorities: number[] | undefined = Form.useWatch('priorities', form);
  const watchedCallCounts: number[] | undefined = Form.useWatch('callCounts', form);

  // A bucket filters either by priority or by call count, never both. Each field is locked only while
  // the other one alone is populated, so a bucket that somehow carries both stays editable.
  const hasPriorities = (watchedPriorities?.length ?? 0) > 0;
  const hasCallCounts = (watchedCallCounts?.length ?? 0) > 0;
  const prioritiesLocked = hasCallCounts && !hasPriorities;
  const callCountsLocked = hasPriorities && !hasCallCounts;

  const takenPriorities = useMemo(() => {
    if (!isActiveForm) return new Set<number>();
    const taken = new Set<number>();
    for (const b of buckets) {
      if (!b.isActive) continue;
      if (editingBucket && b.id === editingBucket.id) continue;
      if (b.salesChannels.some((ch) => selectedChannels.includes(ch))) {
        b.priorities.forEach((p) => taken.add(p));
      }
    }
    return taken;
  }, [buckets, selectedChannels, editingBucket, isActiveForm]);

  const priorityOptions = useMemo(
    () => PRIORITY_OPTIONS.map((o) => ({ ...o, disabled: takenPriorities.has(o.value) })),
    [takenPriorities],
  );

  // Keep values configured outside the offered range (Mongo/script) selectable instead of dropping them.
  const callCountOptions = useMemo(() => {
    const known = new Set(CALL_COUNT_VALUES);
    const extras = (watchedCallCounts ?? [])
      .filter((c) => !known.has(c))
      .sort((a, b) => a - b)
      .map((c) => ({ label: String(c), value: c }));
    return extras.length > 0 ? [...CALL_COUNT_OPTIONS, ...extras] : CALL_COUNT_OPTIONS;
  }, [watchedCallCounts]);

  const load = async () => {
    try {
      setLoading(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const data = await getAutodialerBuckets(token.accessToken);
      if (data) {
        setBuckets(data);
      } else {
        notification.error({ message: labels.loadFailed });
      }
    } catch (e) {
      notification.error({ message: labels.loadFailed, description: String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [instance, inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingBucket(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      uploadFrequency: 'None',
      salesChannels: [],
      priorities: [],
      callCounts: [],
    });
    setDrawerOpen(true);
  };

  const openEdit = (bucket: AutodialerBucketConfiguration) => {
    setEditingBucket(bucket);
    form.setFieldsValue({
      name: bucket.name,
      description: bucket.description,
      autodialerBucketId: bucket.autodialerBucketId,
      autodialerBucketName: bucket.autodialerBucketName,
      salesChannels: bucket.salesChannels,
      priorities: bucket.priorities,
      callCounts: bucket.callCounts ?? [],
      uploadFrequency: bucket.uploadFrequency,
      opportunityStatus: bucket.opportunityStatus,
      isActive: bucket.isActive,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Call counts are omitted when empty: the backend reads null as "no call count filter", while an
    // empty array would survive JSON.stringify and disable the filter without skipping the bucket.
    const { callCounts, ...rest } = values;
    const request: AutodialerBucketConfigurationRequest = {
      ...rest,
      lastModifiedUtc: new Date().toISOString(),
      ...(callCounts && callCounts.length > 0
        ? { callCounts: [...new Set(callCounts)].sort((a, b) => a - b) }
        : {}),
      ...(editingBucket ? { id: editingBucket.id } : {}),
    };

    try {
      setSaving(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);

      if (editingBucket) {
        const result = await updateAutodialerBucket(token.accessToken, editingBucket.id, request);
        if (result === null) {
          notification.error({ message: labels.saveFailed, description: labels.serverError });
          return;
        }
        if (typeof result === 'object' && 'errors' in result) {
          notification.error({ message: labels.validationError, description: result.errors });
          return;
        }
        notification.success({ message: labels.bucketUpdated });
      } else {
        const result = await createAutodialerBucket(token.accessToken, request);
        if (result === null) {
          notification.error({ message: labels.createFailed, description: labels.serverError });
          return;
        }
        if ('errors' in result) {
          notification.error({ message: labels.validationError, description: result.errors });
          return;
        }
        notification.success({ message: labels.bucketCreated });
      }

      setDrawerOpen(false);
      await load();
    } catch (e) {
      notification.error({ message: labels.saveFailed, description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const ok = await deleteAutodialerBucket(token.accessToken, id);
      if (ok) {
        notification.success({ message: labels.bucketDeleted });
        await load();
      } else {
        notification.error({ message: labels.deleteFailed });
      }
    } catch (e) {
      notification.error({ message: labels.deleteFailed, description: String(e) });
    }
  };

  const columns: ColumnsType<AutodialerBucketConfiguration> = [
    {
      title: labels.name,
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: labels.bucketName,
      dataIndex: 'autodialerBucketName',
      key: 'autodialerBucketName',
      width: 130,
    },
    {
      title: labels.salesChannels,
      dataIndex: 'salesChannels',
      key: 'salesChannels',
      width: 160,
      filters: SALES_CHANNEL_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
      onFilter: (value, record) => record.salesChannels.includes(value as string),
      render: (channels: string[]) => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {[...channels].sort().map((c) => <Tag key={c} style={{ margin: 0 }}>{c}</Tag>)}
        </span>
      ),
    },
    {
      title: labels.priorities,
      dataIndex: 'priorities',
      key: 'priorities',
      width: 220,
      render: (p: number[]) => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {[...p].sort((a, b) => a - b).map((n) => <Tag key={n} color={n >= 90 ? 'purple' : n >= 80 ? 'blue' : undefined} style={{ margin: 0 }}>{n}</Tag>)}
        </span>
      ),
    },
    {
      title: 'Call Counts',
      dataIndex: 'callCounts',
      key: 'callCounts',
      width: 160,
      render: (counts?: number[] | null) =>
        counts && counts.length > 0 ? [...counts].sort((a, b) => a - b).join(', ') : 'Any',
    },
    {
      title: labels.frequency,
      dataIndex: 'uploadFrequency',
      key: 'uploadFrequency',
      width: 110,
      filters: uploadFrequencyOptions.map((o) => ({ text: o.label, value: o.value })),
      onFilter: (value, record) => record.uploadFrequency === value,
      render: (freq: UploadFrequency) => {
        const label = uploadFrequencyOptions.find((o) => o.value === freq)?.label ?? freq;
        return <Tag color={freq === 'RealTime' ? 'cyan' : undefined} style={{ margin: 0 }}>{label}</Tag>;
      },
    },
    {
      title: labels.active,
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      filters: [
        { text: labels.active, value: true }, { text: labels.inactive, value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? labels.active : labels.inactive}</Tag>,
    },
    {
      title: labels.lastModified,
      key: 'lastModified',
      width: 200,
      render: (_, record) => (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{new Date(record.lastModifiedUtc).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
          {record.modifiedBy && <Typography.Text type="secondary">{record.modifiedBy}</Typography.Text>}
        </span>
      ),
    },
    {
      title: labels.actions,
      key: 'actions',
      width: 160,
      className: 'autodialer-bucket-actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={(event) => {
            event.stopPropagation();
            openEdit(record);
          }}>
            {labels.edit}
          </Button>
          <Popconfirm
            title={labels.deleteBucket}
            description={labels.deleteWarning}
            onConfirm={() => handleDelete(record.id)}
            okText={labels.delete}
            okButtonProps={{ danger: true }}>
            <Button icon={<DeleteOutlined />} size="small" danger onClick={(event) => event.stopPropagation()}>
              {labels.delete}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const ifPrioRequired = () => {
    var isPrioRequired = true;
    if (uploadFrequencyWatcher === 'Manual') {
      isPrioRequired = false;
    }
    if (watchedBucketName && watchedBucketName.toLowerCase().includes('offtier')) {
      isPrioRequired = false;
    }
    if (hasCallCounts) {
      isPrioRequired = false;
    }
    return isPrioRequired;
  };

  const ifSalesChannelRequired = () => {
    var isSalesChannelRequired = true;
    if (uploadFrequencyWatcher === 'Manual') {
      isSalesChannelRequired = false;
    }

    return isSalesChannelRequired;
  };

  return (
    <div className="autodialer-buckets-config" style={{ padding: 0, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          {strings.autodialerSettings.bucketConfigTab}
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          {labels.addBucket}
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={[...buckets].sort((a, b) => Number(b.isActive) - Number(a.isActive))}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
        tableLayout="fixed"
        rowClassName={(record) => record.isActive ? '' : 'autodialer-bucket-row--inactive'}
        onRow={(record) => ({ onClick: () => openEdit(record) })}
      />
      <Drawer
        title={editingBucket ? labels.editBucket : labels.addBucket}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        footer={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>{labels.cancel}</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              {labels.save}
            </Button>
          </Space>
        }>
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="name" label={labels.name} rules={[{ required: true, message: labels.nameRequired }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={labels.description}>
            <Input />
          </Form.Item>
          <Form.Item
            name="autodialerBucketId"
            label={labels.bucketId}
            rules={[{ required: true, message: labels.bucketIdRequired }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="autodialerBucketName"
            label={labels.bucketName}
            rules={[{ required: true, message: labels.bucketNameRequired }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="salesChannels"
            label={labels.salesChannels}
            rules={[{ required: ifSalesChannelRequired(), message: labels.salesChannelsRequired }]}>
            <Select
              mode="multiple"
              options={SALES_CHANNEL_OPTIONS}
              placeholder={labels.selectSalesChannels}
              onChange={(newChannels: string[]) => {
                if (!form.getFieldValue('isActive')) return;
                const newTaken = new Set<number>();
                for (const b of buckets) {
                  if (!b.isActive) continue;
                  if (editingBucket && b.id === editingBucket.id) continue;
                  if (b.salesChannels.some((ch) => newChannels.includes(ch))) {
                    b.priorities.forEach((p) => newTaken.add(p));
                  }
                }
                const current: number[] = form.getFieldValue('priorities') ?? [];
                form.setFieldValue('priorities', current.filter((p) => !newTaken.has(p)));
              }}
            />
          </Form.Item>
          <Form.Item
            name="priorities"
            label={labels.priorities}
            extra={EXCLUSIVITY_HINT}
            rules={[{ required: ifPrioRequired(), message: 'At least one priority or call count is required' }]}>
            <Select
              mode="multiple"
              options={priorityOptions}
              placeholder={prioritiesLocked ? 'Filtered by call counts' : labels.selectPriorities}
              allowClear
              disabled={prioritiesLocked}
            />
          </Form.Item>
          <Form.Item name="callCounts" label="Call Counts" extra={EXCLUSIVITY_HINT}>
            <Select
              mode="multiple"
              options={callCountOptions}
              placeholder={callCountsLocked ? 'Filtered by priorities' : 'Any call count'}
              allowClear
              disabled={callCountsLocked}
            />
          </Form.Item>
          <Form.Item name="uploadFrequency" label={labels.uploadFrequency} rules={[{ required: true }]}>
            <Select options={uploadFrequencyOptions} />
          </Form.Item>
          <Form.Item name="opportunityStatus" label={labels.opportunityStatus}>
            <Select options={opportunityStatusOptions} placeholder={labels.notSelected} allowClear />
          </Form.Item>
          <Form.Item name="isActive" label={labels.active} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};
